import React, { useState } from 'react';

interface EditPasswordProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<any>;
}

const errorIconSvg = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ verticalAlign: 'middle', display: 'inline-block' }}
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
      fill="#d32f2f"
    />
  </svg>
);

const EditPassword = ({ open, onClose, onSubmit }: EditPasswordProps) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(''); // confirm 필드 에러
  const [newError, setNewError] = useState(''); // 새 비밀번호 필드 에러
  const [currentError, setCurrentError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = () => {
    setCurrentError('');
    setNewError('');
    setError('');

    if (!currentPassword.trim()) {
      setCurrentError('현재 비밀번호를 입력해주세요.');
      return false;
    }
    if (!newPassword.trim()) {
      setNewError('새 비밀번호를 입력해주세요.');
      return false;
    }
    if (!confirmPassword.trim()) {
      setError('새 비밀번호 확인을 입력해주세요.');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('새 비밀번호와 새 비밀번호 확인의 내용이 같지않습니다');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setCurrentError('');
    setNewError('');
    setError('');
    if (!validatePassword()) {
      return;
    }

    setIsLoading(true);
    try {
      const resp = await onSubmit(currentPassword, newPassword);
      // 응답 기반 분기 처리
      if (resp?.status === 'UNAUTHORIZED') {
        setCurrentError('현재 비밀번호가 일치하지 않습니다.');
        return;
      }
      if (resp && resp?.status && resp?.status !== 'OK' && resp?.status !== 200) {
        const apiMsg = resp?.message || '비밀번호 변경에 실패했습니다.';
        setError(apiMsg);
        return;
      }
      // 성공 처리
      handleClose();
      alert('비밀번호가 성공적으로 변경되었습니다.');
    } catch (err: any) {
      // 네트워크/예상치 못한 에러는 공통 에러로 표시
      const apiMsg = err?.response?.data?.message || err?.message || '비밀번호 변경에 실패했습니다.';
      setError(apiMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setCurrentError('');
    onClose();
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.25)',
        zIndex: 1500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      tabIndex={-1}
      aria-modal="true"
      role="dialog"
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          minWidth: 400,
          maxWidth: 640,
          width: '90%',
          boxShadow: '0 2px 16px 0 rgba(0,0,0,0.17)',
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 30px 20px 30px',
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 20,
              color: '#222',
              letterSpacing: '-0.4px',
              fontFamily: 'Noto Sans KR',
              fontStyle: 'normal',
              lineHeight: '140%',
            }}
          >
            비밀번호 변경
          </span>
          <button
            onClick={handleClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              margin: -4,
              fontSize: 20,
              color: '#888',
              lineHeight: 1,
              borderRadius: 4,
              transition: 'background 0.2s',
            }}
            tabIndex={0}
          >
            <span style={{ fontSize: 24, fontWeight: 400, display: 'inline-block', lineHeight: 1 }}>×</span>
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '24px 24px 0 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          {/* 현재 비밀번호 */}
          <div style={{ marginBottom: 0 }}>
            <label
              htmlFor="editpw-current"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                fontSize: '14px',
                color: '#262728',
                fontFamily: 'Noto Sans KR',
                fontStyle: 'normal',
                lineHeight: '150%',
                letterSpacing: '-0.28px',
              }}
            >
              현재 비밀번호
            </label>
            <input
              id="editpw-current"
              type="password"
              placeholder="*****"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              maxLength={300}
              style={{
                width: '100%',
                padding: '11px 12px',
                fontSize: 15,
                border: currentError ? '1.5px solid #d32f2f' : '1px solid #e0e0e0',
                borderRadius: 6,

                outline: 'none',
                transition: 'border 0.2s',
                marginBottom: 0,
              }}
              autoComplete="current-password"
              aria-invalid={Boolean(currentError)}
            />
            {currentError ? (
              <div
                style={{
                  color: '#d32f2f',
                  marginTop: 6,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 400,
                  letterSpacing: '-0.2px',
                  minHeight: 20,
                }}
              >
                {currentError}
              </div>
            ) : null}
          </div>
          {/* 새 비밀번호 */}
          <div style={{ marginBottom: 0 }}>
            <label
              htmlFor="editpw-new"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                fontSize: '14px',
                color: '#262728',
                fontFamily: 'Noto Sans KR',
                fontStyle: 'normal',
                lineHeight: '150%',
                letterSpacing: '-0.28px',
              }}
            >
              새 비밀번호
            </label>
            <input
              id="editpw-new"
              type="password"
              placeholder="*****"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              maxLength={300} // 새 비밀번호 최대 길이 제한
              style={{
                width: '100%',
                padding: '11px 12px',
                fontSize: 15,
                border: newError ? '1.5px solid #d32f2f' : '1px solid #e0e0e0',
                borderRadius: 6,
                outline: 'none',
                transition: 'border 0.2s',
                marginBottom: 0,
              }}
              autoComplete="new-password"
              aria-invalid={Boolean(newError)}
            />
            {newError ? (
              <div
                style={{
                  color: '#d32f2f',
                  marginTop: 6,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 400,
                  letterSpacing: '-0.2px',
                  minHeight: 20,
                }}
              >
                {newError}
              </div>
            ) : null}
          </div>
          {/* 새 비밀번호 확인 */}
          <div style={{ marginBottom: error ? 0 : 0, position: 'relative' }}>
            <label
              htmlFor="editpw-confirm"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                fontSize: '14px',
                color: '#262728',
                fontFamily: 'Noto Sans KR',
                fontStyle: 'normal',
                lineHeight: '150%',
                letterSpacing: '-0.28px',
              }}
            >
              새 비밀번호 확인
            </label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                id="editpw-confirm"
                type="password"
                placeholder="****"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                maxLength={300} // 새 비밀번호 확인 최대 길이 제한
                style={{
                  width: '100%',
                  padding: error ? '11px 36px 11px 12px' : '11px 12px',
                  fontSize: 15,
                  border: error ? '1.5px solid #d32f2f' : '1px solid #e0e0e0',
                  borderRadius: 6,

                  outline: 'none',
                  transition: 'border 0.2s',
                  marginBottom: 0,
                  boxSizing: 'border-box',
                }}
                autoComplete="new-password"
                aria-invalid={Boolean(error)}
              />
              {error ? (
                <span
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  aria-hidden="true"
                >
                  {errorIconSvg}
                </span>
              ) : null}
            </div>
            {error ? (
              <div
                style={{
                  color: '#d32f2f',
                  marginTop: 6,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 400,
                  letterSpacing: '-0.2px',
                  minHeight: 20,
                }}
              >
                {error}
              </div>
            ) : null}
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            padding: '16px 24px 22px 24px',
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              fontFamily: 'Noto Sans KR',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '100%',
              letterSpacing: '-0.28px',
              width: '60px',
              height: '40px',
              border: '1px solid #e0e0e0',
              background: '#fff',
              color: '#666',
              borderRadius: '3px',
              cursor: 'pointer',
              transition: 'background 0.2s, border 0.2s',
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            style={{
              width: '60px',
              height: '40px',
              border: 'none',
              background: isLoading ? '#e0e0e0' : '#004CBD',
              color: '#fff',
              borderRadius: '3px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.85 : 1,
              fontFamily: 'Noto Sans KR',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '100%',
              letterSpacing: '-0.28px',
            }}
          >
            {isLoading ? '변경 중...' : '확인'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditPassword;
